import { useState, useEffect, useMemo } from 'react'
import { toast } from 'sonner'
import { apiClient, useAuthStore } from '@indipia/shared'
import type { NavFn } from '../pages/SchedulerPage'
import { ErrorBanner } from '../components/AsyncState'
import { useCommonCodes } from '../context/CommonCodesContext'
import { getSiteStatusBadge, SITE_STATUS_MAP } from '../constants/siteStatus'
import { Button, Search, Pagination, Badge, Modal, FilterChip, DatePicker, StatusHint } from '@indipia/ui-core'
import { endDateShortenHint } from '../constants/periodSync.logic'
import { FieldReportStatusPopup } from '../components/FieldReportStatusPopup'
import { useModalA11y } from '../hooks/useModalA11y'
import { useScheduleRequestCount } from '../hooks/useScheduleRequestCount'
import { buildScheduleReplaceNotice } from '../constants/scheduleReplaceNotice.logic'
import { Z_POPUP, Z_CONTEXT_MENU } from '../constants/zIndex'

type Props = { navigate: NavFn; onSiteChanged?: () => void }

interface ScheduleItem {
  scheduleId:     number
  siteId:         number
  siteName:       string
  customerName:   string
  contact:        string
  startDate:      string
  endDate:        string
  scheduleStatus: string
  taskCount?:     number
}

function calcProgress(startDate: string, endDate: string): number {
  if (!startDate || !endDate) return 0
  const now   = Date.now()
  const start = new Date(startDate).getTime()
  const end   = new Date(endDate).getTime()
  if (end <= start) return 0
  if (now <= start) return 0
  if (now >= end)   return 100
  return Math.round((now - start) / (end - start) * 100)
}

const STATUS_ORDER: Record<string, number> = { READY: 0, IN_PROGRESS: 1, DONE: 2 }

const PAGE_SIZE = 15

function fmtDate(d: string) {
  if (!d) return '—'
  const [y, m, day] = d.split('-')
  return `${y}.${m}.${day}`
}

export function SiteListView({ navigate, onSiteChanged }: Props) {
  const user = useAuthStore(s => s.user)
  const { siteStatuses } = useCommonCodes()
  const statusLabel = (code: string) => siteStatuses.find(c => c.code === code)?.codeName || SITE_STATUS_MAP[code]?.label || code
  const STATUS_FILTERS = useMemo(() => [
    { key: '', label: '전체' },
    ...(['READY', 'IN_PROGRESS', 'DONE'] as const).map(code => ({ key: code, label: statusLabel(code) })),
  ], [siteStatuses])
  const [schedules,    setSchedules]    = useState<ScheduleItem[]>([])
  const [loading,      setLoading]      = useState(false)
  const [search,       setSearch]       = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [statusSite,   setStatusSite]   = useState<ScheduleItem | null>(null)
  // 상태 칼럼 드롭다운 — 표가 overflow(가로 스크롤)를 쓰므로 absolute 로 두면 잘린다.
  // 트리거 좌표를 재서 fixed 로 띄운다.
  const [statusMenu,   setStatusMenu]   = useState<{ site: ScheduleItem; top: number; left: number } | null>(null)
  const [completing,   setCompleting]   = useState<number | null>(null)
  const [confirmSite,  setConfirmSite]  = useState<ScheduleItem | null>(null)
  const [deleteSite,   setDeleteSite]   = useState<ScheduleItem | null>(null)
  const [deleting,     setDeleting]     = useState<number | null>(null)
  const [resetSite,    setResetSite]    = useState<ScheduleItem | null>(null)
  const deleteNotice = buildScheduleReplaceNotice({ mode: 'delete',  requestCount: useScheduleRequestCount(deleteSite?.scheduleId) })
  const resetNotice  = buildScheduleReplaceNotice({ mode: 'replace', requestCount: useScheduleRequestCount(resetSite?.scheduleId) })
  const [page,         setPage]         = useState(0)
  const [loadError,    setLoadError]    = useState(false)
  const [reloadKey,    setReloadKey]    = useState(0)
  const [editSite,     setEditSite]     = useState<ScheduleItem | null>(null)
  const [editStart,    setEditStart]    = useState('')
  const [editEnd,      setEditEnd]      = useState('')
  const [editError,    setEditError]    = useState('')
  const [editSaving,   setEditSaving]   = useState(false)
  // 공사기간 수정 다이얼로그 — ui-core Modal은 overflow:hidden이라 DatePicker 팝오버
  // 캘린더가 잘려서 이 다이얼로그만 별도의 가벼운 오버레이로 직접 구현한다.
  // 포커스트랩·Esc 닫기·포커스 이동/복원은 자매 팝업들과 동일하게 useModalA11y로 처리.
  const editModal = useModalA11y(editSite != null, () => { if (!editSaving) setEditSite(null) })

  // 상태 드롭다운 — 바깥 클릭·Esc·스크롤/리사이즈로 닫는다(좌표가 어긋나므로 추적 대신 닫기)
  useEffect(() => {
    if (!statusMenu) return
    const close = () => setStatusMenu(null)
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setStatusMenu(null) }
    document.addEventListener('mousedown', close)
    document.addEventListener('keydown', onKey)
    window.addEventListener('scroll', close, true)
    window.addEventListener('resize', close)
    return () => {
      document.removeEventListener('mousedown', close)
      document.removeEventListener('keydown', onKey)
      window.removeEventListener('scroll', close, true)
      window.removeEventListener('resize', close)
    }
  }, [statusMenu])

  useEffect(() => {
    setLoading(true)
    setLoadError(false)
    const headers = user?.compId ? { 'X-Comp-Id': String(user.compId) } : {}
    apiClient
      .get<{ data: { schedules: ScheduleItem[] } }>('/schedule/schedules', {
        params: { pageSize: 100 },
        headers,
      })
      .then(res => {
        const list = res.data.data?.schedules ?? []
        setSchedules([...list].sort((a, b) => b.scheduleId - a.scheduleId))
      })
      .catch(() => { setSchedules([]); setLoadError(true) })
      .finally(() => setLoading(false))
  }, [user?.compId, reloadKey])

  const handleComplete = (s: ScheduleItem) => setConfirmSite(s)

  const handleConfirmComplete = async () => {
    if (!confirmSite) return
    const s = confirmSite
    setConfirmSite(null)
    setCompleting(s.scheduleId)
    try {
      const headers = user?.compId ? { 'X-Comp-Id': String(user.compId) } : {}
      await apiClient.post(`/schedule/schedules/${s.scheduleId}/complete`, {}, { headers })
      setSchedules(prev => prev.map(item =>
        item.scheduleId === s.scheduleId ? { ...item, scheduleStatus: 'DONE' } : item
      ))
      // 홈/스케줄 관리 우측 현장 목록(SchedulerPage의 allSites)도 갱신되도록 알린다 —
      // 안 그러면 새로고침 전까지 시공완료 처리 전 상태로 남아있다.
      onSiteChanged?.()
    } catch {
      toast.error('상태 변경에 실패했습니다.')
    } finally {
      setCompleting(null)
    }
  }

  const handleConfirmDelete = async () => {
    if (!deleteSite) return
    const s = deleteSite
    setDeleteSite(null)
    setDeleting(s.scheduleId)
    try {
      const headers = user?.compId ? { 'X-Comp-Id': String(user.compId) } : {}
      await apiClient.delete(`/schedule/schedules/${s.scheduleId}`, { headers })
      setSchedules(prev => prev.filter(item => item.scheduleId !== s.scheduleId))
      onSiteChanged?.()
    } catch {
      toast.error('삭제에 실패했습니다.')
    } finally {
      setDeleting(null)
    }
  }

  const handleOpenEditPeriod = (s: ScheduleItem) => {
    setEditSite(s)
    setEditStart(s.startDate?.slice(0, 10) ?? '')
    setEditEnd(s.endDate?.slice(0, 10) ?? '')
    setEditError('')
  }

  const handleSavePeriod = async () => {
    if (!editSite) return
    if (!editStart || !editEnd) { setEditError('공사 시작일과 종료일을 모두 선택하세요.'); return }
    if (editEnd < editStart) { setEditError('종료일은 시작일 이후여야 합니다.'); return }
    const s = editSite
    setEditSaving(true)
    setEditError('')
    try {
      const headers = user?.compId ? { 'X-Comp-Id': String(user.compId) } : {}
      await apiClient.put(`/schedule/schedules/${s.scheduleId}`, { startDate: editStart, endDate: editEnd }, { headers })
      setSchedules(prev => prev.map(item =>
        item.scheduleId === s.scheduleId ? { ...item, startDate: editStart, endDate: editEnd } : item
      ))
      onSiteChanged?.()
      setEditSite(null)
    } catch {
      setEditError('공사기간 저장에 실패했습니다.')
    } finally {
      setEditSaving(false)
    }
  }

  // 검색어·상태 필터가 바뀌면 첫 페이지로 리셋 — 안 그러면 결과가 줄어든 뒤 빈 페이지에 머물 수 있다.
  useEffect(() => { setPage(0) }, [search, statusFilter])

  const filtered = schedules
    .filter(s => {
      if (statusFilter && s.scheduleStatus !== statusFilter) return false
      if (search) {
        const q = search.toLowerCase()
        if (!s.siteName.toLowerCase().includes(q) && !s.customerName.toLowerCase().includes(q)) return false
      }
      return true
    })
    // 준비중 → 시공중 → 시공완료 순, 동일 상태면 공사기간(시작일) 내림차순
    .sort((a, b) => {
      const aOrder = STATUS_ORDER[a.scheduleStatus] ?? 3
      const bOrder = STATUS_ORDER[b.scheduleStatus] ?? 3
      if (aOrder !== bOrder) return aOrder - bOrder
      return (b.startDate ?? '').localeCompare(a.startDate ?? '')
    })

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))
  const safePage  = Math.min(page, pageCount - 1)
  const paged     = filtered.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE)

  return (
    <div className="p-6">
      {/* 필터 행 */}
      <div className="mb-4 flex flex-wrap items-center gap-2.5">
        {/* 검색 */}
        <Search
          value={search}
          onChange={setSearch}
          placeholder="현장명 / 고객명 검색"
          className="min-w-[200px] flex-1"
        />

        {/* 상태 필터 칩 */}
        {STATUS_FILTERS.map(f => {
          const active = statusFilter === f.key
          return (
            <FilterChip
              key={f.key}
              active={active}
              onClick={() => setStatusFilter(active ? '' : f.key)}
            >
              {f.label}
            </FilterChip>
          )
        })}
      </div>

      {/* 테이블 */}
      <div className="overflow-hidden rounded-xxl border border-border bg-surface-raised" style={{ overflowX: 'auto' }}>
        <table className="w-full border-separate border-spacing-0 text-[length:var(--type-caption-size)]" style={{ tableLayout: 'fixed', minWidth: 900 }}>
          {/* 칼럼 폭 전부 고정 — 칼럼 사이 간격이 항상 동일하게 유지된다. */}
          <colgroup>
            <col style={{ width: 250 }} />
            <col style={{ width: 100 }} />
            {/* 공사기간 — 기간 텍스트 + [수정] 버튼 */}
            <col style={{ width: 225 }} />
            <col style={{ width: 200 }} />
            {/* 상태 — 뱃지 + 드롭다운 caret */}
            <col style={{ width: 120 }} />
            <col style={{ width: 70 }} />
            <col style={{ width: 190 }} />
            {/* 액션 — 아이콘 대신 "초기화"·"삭제" 글씨가 들어갈 폭 */}
            <col style={{ width: 150 }} />
          </colgroup>
          <thead>
            <tr>
              {[
                { h: '현장명',   align: 'left'   },
                { h: '고객',     align: 'left'   },
                { h: '공사기간', align: 'left'   },
                { h: '진행률',   align: 'left'   },
                { h: '상태',     align: 'left'   },
                { h: '공종수',   align: 'center' },
                { h: '보기',     align: 'center' },
                { h: '액션',     align: 'center' },
              ].map(({ h, align }) => (
                <th
                  key={h}
                  className="whitespace-nowrap border-b border-border bg-surface px-3.5 py-2.5 text-[length:var(--type-caption-size)] font-bold text-text-muted"
                  style={{ textAlign: align as 'left' | 'center' }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr aria-busy="true"><td colSpan={8} className="py-8 text-center text-[12px] text-text-muted">불러오는 중...</td></tr>
            )}
            {!loading && loadError && (
              <tr><td colSpan={8} className="p-4">
                <ErrorBanner message="현장 목록을 불러오지 못했습니다." onRetry={() => setReloadKey(k => k + 1)} />
              </td></tr>
            )}
            {!loading && !loadError && filtered.length === 0 && (
              <tr><td colSpan={8} className="py-8 text-center text-[12px] text-text-muted">
                {search || statusFilter ? '검색 결과가 없습니다' : '등록된 현장이 없습니다'}
              </td></tr>
            )}
            {!loading && !loadError && paged.map(s => {
              const st   = getSiteStatusBadge(s.scheduleStatus, siteStatuses.find(c => c.code === s.scheduleStatus)?.codeName)
              const prog = calcProgress(s.startDate, s.endDate)
              return (
                <tr
                  key={s.scheduleId}
                  className="cursor-pointer hover:[&>td]:bg-surface"
                >
                  <td className="overflow-hidden text-ellipsis whitespace-nowrap border-b border-border px-3.5 py-[11px] font-bold text-text">
                    {s.siteName || '—'}
                  </td>
                  <td className="overflow-hidden text-ellipsis whitespace-nowrap border-b border-border px-3.5 py-[11px] text-text-muted">
                    {s.customerName || '—'}
                  </td>
                  {/* 공사기간 — 값 옆에 수정 버튼을 둬서 무엇을 바꾸는 버튼인지 바로 읽히게 한다 */}
                  <td className="whitespace-nowrap border-b border-border px-3.5 py-[11px]">
                    <div className="flex items-center gap-1.5">
                      <span className="text-[length:var(--type-caption-size)] text-text-muted">
                        {fmtDate(s.startDate)} ~ {fmtDate(s.endDate)}
                      </span>
                      <button
                        onClick={e => { e.stopPropagation(); handleOpenEditPeriod(s) }}
                        title="공사기간 수정"
                        className="flex shrink-0 items-center gap-1 whitespace-nowrap rounded-xl border-[1.5px] border-border bg-surface-raised px-2 py-0.5 text-[length:var(--type-caption-size)] text-text-muted"
                      >
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                        </svg>
                        수정
                      </button>
                    </div>
                  </td>
                  <td className="border-b border-border px-3.5 py-[11px]">
                    <div className="flex min-w-[100px] items-center gap-2">
                      <div className="h-[5px] flex-1 overflow-hidden rounded-full bg-surface">
                        <div className="h-full rounded-full bg-group" style={{ width: `${prog}%` }} />
                      </div>
                      <span className="min-w-[32px] text-right text-[length:var(--type-caption-size)] font-bold text-group">{prog}%</span>
                    </div>
                  </td>
                  {/* 상태 — 뱃지를 누르면 아래로 메뉴가 열리고 거기서 시공완료 처리 */}
                  <td className="border-b border-border px-3.5 py-[11px]">
                    {s.scheduleStatus === 'DONE' ? (
                      <Badge status={st.status} className="whitespace-nowrap !text-[10.5px]">{st.label}</Badge>
                    ) : (
                      <button
                        onMouseDown={e => e.stopPropagation()}
                        onClick={e => {
                          e.stopPropagation()
                          if (statusMenu?.site.scheduleId === s.scheduleId) { setStatusMenu(null); return }
                          const r = (e.currentTarget as HTMLElement).getBoundingClientRect()
                          setStatusMenu({ site: s, top: r.bottom + 4, left: r.left })
                        }}
                        disabled={completing === s.scheduleId}
                        aria-haspopup="menu"
                        aria-expanded={statusMenu?.site.scheduleId === s.scheduleId}
                        title="상태 변경"
                        className="flex items-center gap-1"
                        style={{ opacity: completing === s.scheduleId ? 0.6 : 1 }}
                      >
                        <Badge status={st.status} className="whitespace-nowrap !text-[10.5px]">{st.label}</Badge>
                        <svg
                          width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                          strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"
                          className="shrink-0 text-text-muted transition-transform"
                          style={{ transform: statusMenu?.site.scheduleId === s.scheduleId ? 'rotate(180deg)' : undefined }}
                        >
                          <polyline points="6 9 12 15 18 9"/>
                        </svg>
                      </button>
                    )}
                  </td>
                  <td className="border-b border-border px-3.5 py-[11px] text-center text-[12px] text-text-muted">
                    {s.taskCount !== undefined ? s.taskCount : '—'}
                  </td>
                  {/* 보기 컬럼 */}
                  <td className="border-b border-border px-3.5 py-[11px]">
                    <div className="flex justify-center gap-1.5">
                      <Button
                        variant="secondary"
                        size="sm"
                        className="!h-auto gap-1 !px-2 !py-1 text-[length:var(--type-caption-size)]"
                        onClick={e => { e.stopPropagation(); setStatusSite(s) }}
                        title="공사현황보기"
                      >
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
                        </svg>
                        공사현황
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        className="!h-auto gap-1 !px-2 !py-1 text-[length:var(--type-caption-size)]"
                        onClick={e => { e.stopPropagation(); navigate('gantt', s.scheduleId) }}
                        title="스케줄"
                      >
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M9 21V9"/>
                        </svg>
                        스케줄
                      </Button>
                    </div>
                  </td>
                  {/* 액션 컬럼: 초기화 · 삭제 (공사기간·시공완료는 각 칼럼에서 처리) */}
                  <td className="border-b border-border px-3.5 py-[11px]">
                    <div className="flex justify-center gap-1.5">
                      <button
                        onClick={e => { e.stopPropagation(); setResetSite(s) }}
                        className="whitespace-nowrap rounded-xl border-[1.5px] border-border bg-surface-raised px-2.5 py-1 text-[length:var(--type-caption-size)] text-text-muted"
                        title="초기화"
                      >
                        초기화
                      </button>
                      <button
                        onClick={e => { e.stopPropagation(); setDeleteSite(s) }}
                        disabled={deleting === s.scheduleId}
                        className="whitespace-nowrap rounded-xl border-[1.5px] px-2.5 py-1 text-[length:var(--type-caption-size)] text-danger-600"
                        title="삭제"
                        style={{
                          opacity: deleting === s.scheduleId ? 0.5 : 1,
                          borderColor: 'color-mix(in srgb, var(--color-error) 25%, white)',
                          background: 'color-mix(in srgb, var(--color-error) 8%, white)',
                        }}
                      >
                        삭제
                      </button>
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {/* 건수 + 페이지네이션 */}
      {!loading && filtered.length > 0 && (
        <div className="mt-2.5 grid grid-cols-3 items-center">
          <div className="text-[length:var(--type-caption-size)] text-text-muted">
            총 {filtered.length}건
            {schedules.length !== filtered.length && ` (전체 ${schedules.length}건)`}
          </div>
          <div className="flex justify-center">
            {pageCount > 1 && (
              <Pagination page={safePage + 1} pageCount={pageCount} onChange={p => setPage(p - 1)} />
            )}
          </div>
          <div />
        </div>
      )}

      {/* 상태 드롭다운 — 표의 overflow 에 잘리지 않도록 fixed 로 띄운다 */}
      {statusMenu && (
        <div
          role="menu"
          onMouseDown={e => e.stopPropagation()}
          style={{
            position: 'fixed', top: statusMenu.top, left: statusMenu.left, zIndex: Z_CONTEXT_MENU,
            minWidth: 148, background: 'var(--shell-surface)', border: '1px solid var(--shell-border)',
            borderRadius: 12, boxShadow: 'var(--elevation-2)', overflow: 'hidden', padding: 4,
          }}
        >
          <button
            role="menuitem"
            onClick={() => { const s = statusMenu.site; setStatusMenu(null); handleComplete(s) }}
            className="flex w-full items-center gap-2 whitespace-nowrap rounded-lg px-2.5 py-2 text-left text-[12px] text-text hover:bg-surface"
          >
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--color-success)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="shrink-0">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
            시공완료 처리
          </button>
        </div>
      )}

      {/* 공사현황 팝업 */}
      {statusSite && (
        <FieldReportStatusPopup
          onClose={() => setStatusSite(null)}
          siteId={statusSite.siteId}
          siteName={statusSite.siteName}
        />
      )}

      {/* ── 공사기간 수정 다이얼로그 ──────────────────────────────── */}
      {/* ui-core Modal은 overflow:hidden이라 DatePicker 팝오버가 잘리므로,
          이 다이얼로그만 overflow:visible인 가벼운 오버레이로 직접 구현 */}
      {editSite && (
        <div
          onClick={() => !editSaving && setEditSite(null)}
          style={{ position: 'fixed', inset: 0, background: 'var(--alpha-black-500)', zIndex: Z_POPUP, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <div
            ref={editModal.boxRef}
            role="dialog"
            aria-modal="true"
            aria-label="공사기간 수정"
            onClick={e => e.stopPropagation()}
            style={{ background: 'var(--shell-surface)', color: 'var(--shell-on-surface)', borderRadius: 24, boxShadow: 'var(--elevation-3)', width: '100%', maxWidth: 420, padding: '20px 24px 18px', overflow: 'visible' }}
          >
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--shell-on-surface)', marginBottom: 4 }}>공사기간 수정</div>
              <div style={{ fontSize: 'var(--type-caption-size)', color: 'var(--shell-on-surface-2)' }}>
                <strong>"{editSite.siteName}"</strong> 현장의 공사 기간을 변경합니다.
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: editError ? 8 : 0 }}>
              <DatePicker label="시작일" value={editStart} onChange={setEditStart} disabled={editSaving} />
              <DatePicker label="종료일" value={editEnd} onChange={setEditEnd} disabled={editSaving} />
            </div>
            {/* 종료일을 바꾸면 서버가 공사완료 마커·공유 링크 만료일도 함께 바꾼다 — 막지 않고 알린다(C-3). 단축이면 한 줄 더 분명히 */}
            {editEnd && editEnd !== editSite.endDate?.slice(0, 10) && (
              <div style={{ marginTop: 10 }}>
                <StatusHint tone={endDateShortenHint(editSite.endDate, editEnd) ? 'warning' : 'info'}>
                  {endDateShortenHint(editSite.endDate, editEnd) ?? '종료일을 바꾸면 공사완료 마커와 공유 링크 만료일도 함께 바뀝니다.'}
                </StatusHint>
              </div>
            )}
            {editError && (
              <div style={{ fontSize: 'var(--type-caption-size)', color: 'var(--status-danger-600)', marginTop: 8, marginBottom: 4 }}>{editError}</div>
            )}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 16 }}>
              <Button variant="secondary" size="sm" onClick={() => setEditSite(null)} disabled={editSaving}>취소</Button>
              <Button variant="primary" size="sm" onClick={handleSavePeriod} disabled={editSaving}>
                {editSaving ? '저장 중...' : '저장'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ── 초기화 확인 다이얼로그 ────────────────────────────────── */}
      <Modal
        open={resetSite != null}
        onClose={() => setResetSite(null)}
        footer={resetSite && (
          <>
            <Button variant="secondary" onClick={() => setResetSite(null)}>취소</Button>
            <Button
              variant="primary"
              onClick={() => { const s = resetSite; setResetSite(null); navigate('create-upload', undefined, undefined, undefined, s.siteId) }}
            >초기화</Button>
          </>
        )}
      >
        {resetSite && (
          <>
            <div style={{ margin: 'calc(-1 * var(--spacing-05)) calc(-1 * var(--spacing-05)) 0', background: 'color-mix(in srgb, var(--color-warning) 10%, white)', padding: '22px 24px 16px', borderBottom: '1px solid color-mix(in srgb, var(--color-warning) 25%, white)', display: 'flex', alignItems: 'flex-start', gap: 12 }}>
              <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'var(--color-warning)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--shell-surface)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 .49-3.5"/>
                </svg>
              </div>
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--color-warning)', marginBottom: 4 }}>스케줄 초기화</div>
                <div style={{ fontSize: 'var(--type-caption-size)', color: 'var(--color-warning)', lineHeight: 1.6 }}>
                  <strong>"{resetSite.siteName}"</strong> 현장의 스케줄을<br />새로 작성합니다. 계속 하시겠습니까?
                  {resetNotice && <><br />{resetNotice}</>}
                </div>
              </div>
            </div>
          </>
        )}
      </Modal>

      {/* ── 삭제 확인 다이얼로그 ──────────────────────────────────── */}
      <Modal
        open={deleteSite != null}
        onClose={() => setDeleteSite(null)}
        footer={deleteSite && (
          <>
            <Button variant="secondary" onClick={() => setDeleteSite(null)}>취소</Button>
            <Button variant="primary" onClick={handleConfirmDelete}>삭제</Button>
          </>
        )}
      >
        {deleteSite && (
          <>
            <div style={{ margin: 'calc(-1 * var(--spacing-05)) calc(-1 * var(--spacing-05)) 0', background: 'color-mix(in srgb, var(--color-error) 8%, white)', padding: '22px 24px 16px', borderBottom: '1px solid color-mix(in srgb, var(--color-error) 25%, white)', display: 'flex', alignItems: 'flex-start', gap: 12 }}>
              <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'var(--color-error)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--shell-surface)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
                </svg>
              </div>
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--status-danger-600)', marginBottom: 4 }}>스케줄 삭제</div>
                <div style={{ fontSize: 'var(--type-caption-size)', color: 'var(--status-danger-600)', lineHeight: 1.6 }}>
                  <strong>"{deleteSite.siteName}"</strong> 현장의 스케줄을 삭제합니다.<br />
                  삭제된 데이터는 복구할 수 없습니다.
                  {deleteNotice && <><br />{deleteNotice}</>}
                </div>
              </div>
            </div>
          </>
        )}
      </Modal>

      {/* ── 시공완료 확인 다이얼로그 ───────────────────────────────── */}
      <Modal
        open={confirmSite != null}
        onClose={() => setConfirmSite(null)}
        footer={confirmSite && (
          <>
            <Button variant="secondary" onClick={() => setConfirmSite(null)}>취소</Button>
            <Button
              variant="primary"
              onClick={handleConfirmComplete}
            >시공완료 처리</Button>
          </>
        )}
      >
        {confirmSite && (
          <>
            <div style={{ margin: 'calc(-1 * var(--spacing-05)) calc(-1 * var(--spacing-05)) 0', background: 'color-mix(in srgb, var(--color-success) 8%, white)', padding: '22px 24px 16px', borderBottom: '1px solid color-mix(in srgb, var(--color-success) 25%, white)', display: 'flex', alignItems: 'flex-start', gap: 12 }}>
              <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'var(--color-success)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--shell-surface)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
              </div>
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--color-success)', marginBottom: 4 }}>시공완료 처리</div>
                <div style={{ fontSize: 'var(--type-caption-size)', color: 'var(--color-success)', lineHeight: 1.6 }}>
                  아래 현장을 <strong>시공완료</strong> 상태로 변경합니다.<br />
                  완료 처리 후에는 되돌리기 어렵습니다.
                </div>
              </div>
            </div>
          </>
        )}
      </Modal>
    </div>
  )
}
